import React from 'react';

import { Box, Typography, useTheme } from '@material-ui/core';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { getFriendNames } from 'utils/gameDetails/getFriendNames';

import { User } from 'services/api/definitions/account.definitions';

import GameIcon from 'components/@common/GameIcon';
import { canCopy, canShare, handleCopyLink, handleOnShare } from 'components/@common/Share/util';

import ImageGroup from './components/ImageGroup';

const { publicRuntimeConfig: config } = getConfig();

const StyledTypography = styled(Typography)`
  text-decoration: underline;
  &:hover {
    cursor: pointer;
  }
`;

interface Props {
  friends: User[];
  avatar: string;
  shareTitle: string;
}

const Friends: React.FC<Props> = ({ friends, avatar, shareTitle }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { asPath } = useRouter();

  const handleShareClick = async () => {
    const url = config.CLIENT_URL + asPath;

    if (canShare()) {
      handleOnShare({
        title: intl.formatMessage({ id: 'seo.title_template' }),
        url,
        text: intl.formatMessage(
          { id: 'game.details.friends_playing.share.text' },
          { title: shareTitle },
        ),
      });
    } else if (!canShare() && canCopy()) {
      handleCopyLink(url);
      enqueueSnackbar(intl.formatMessage({ id: 'friends.page.copy_link.message' }));
    }
  };

  if (!friends.length) {
    return (
      <Box display="flex" alignItems="center">
        <GameIcon src={avatar} borderRadius="50%" size={32} mr={4} imagesAboveFold />
        <Box color="text.secondary" clone>
          <Typography variant="caption">
            <FormattedMessage
              id="game.details.friends_playing.zero"
              values={{
                b: (v: string) => (
                  <Box fontWeight="bold" color="text.primary" onClick={handleShareClick} clone>
                    <StyledTypography variant="caption" color="secondary">
                      {v}
                    </StyledTypography>
                  </Box>
                ),
              }}
            />
          </Typography>
        </Box>
      </Box>
    );
  }

  const names = getFriendNames(friends);

  return (
    <Box display="flex" alignItems="center">
      <Box mr={4}>
        <ImageGroup
          images={friends.map(friend => ({
            url: friend.avatar,
            alt: friend.username,
          }))}
        />
      </Box>
      <Typography variant="caption">
        <FormattedMessage
          id={names.id}
          values={{
            ...names.values,
            b: (v: string) => <b style={{ color: theme.palette.text.primary }}>{v}</b>,
          }}
        />
      </Typography>
    </Box>
  );
};

export default Friends;
